import React from "react";
import { Sparkles, Pill, Beer, ShoppingBag, CheckCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";
import DashboardImage from "../../assets/images/dashboard.png";
import PharmacyImage from "../../assets/images/dashboard-pharmacy.png";
import BarImage from "../../assets/images/dashboard-bar.png";
import RetailImage from "../../assets/images/dashboard-retail.png";

const SolutionsSection: React.FC = () => {
  const { t } = useLanguage();

  const solutions = [
    {
      id: "lite",
      titleKey: "solution_lite_title",
      descriptionKey: "solution_lite_description",
      icon: <Sparkles className="h-6 w-6 text-white" />,
      features: [
        "solution_lite_feat1",
        "solution_lite_feat2",
        "solution_lite_feat3",
      ],
      image: DashboardImage,
      color: "bg-emerald-600",
      accent: "text-emerald-600",
      bgClass: "bg-emerald-50/50",
      link: "https://app.fukkoni.com/register?plan=lite"
    },
    {
      id: "pharmacy",
      titleKey: "solution_pharmacy_title",
      descriptionKey: "solution_pharmacy_description",
      icon: <Pill className="h-6 w-6 text-white" />,
      features: [
        "solution_pharmacy_feat1",
        "solution_pharmacy_feat2",
        "solution_pharmacy_feat3",
      ],
      image: PharmacyImage,
      color: "bg-teal-500",
      accent: "text-teal-600",
      bgClass: "bg-teal-50/50",
      link: "https://app.fukkoni.com/register"
    },
    {
      id: "bar",
      titleKey: "solution_bar_title",
      descriptionKey: "solution_bar_description",
      icon: <Beer className="h-6 w-6 text-white" />,
      features: [
        "solution_bar_feat1",
        "solution_bar_feat2",
        "solution_bar_feat3",
      ],
      image: BarImage,
      color: "bg-indigo-500",
      accent: "text-indigo-600",
      bgClass: "bg-white",
      link: "https://app.fukkoni.com/register"
    },
    {
      id: "retail",
      titleKey: "solution_retail_title",
      descriptionKey: "solution_retail_description",
      icon: <ShoppingBag className="h-6 w-6 text-white" />,
      features: [
        "solution_retail_feat1",
        "solution_retail_feat2",
        "solution_retail_feat3",
      ],
      image: RetailImage,
      color: "bg-blue-500",
      accent: "text-blue-600",
      bgClass: "bg-blue-50/50",
      link: "https://app.fukkoni.com/register"
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden" id="solutions">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6 border border-primary/20">
            {t("solutions_badge")}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
            {t("solutions_title_part1")}{" "}
            <span className="text-primary">{t("solutions_title_part2")}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {t("solutions_description")}
          </p>
        </div>

        <div className="space-y-24">
          {solutions.map((solution, idx) => (
            <div
              key={idx}
              className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 ${
                idx % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Text Content */}
              <div className="lg:w-1/2 space-y-8">
                <div className={`inline-flex p-3 rounded-2xl shadow-lg ${solution.color}`}>
                  {solution.icon}
                </div>
                
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                  {t(solution.titleKey)}
                </h3>
                
                <p className="text-lg text-slate-600 leading-relaxed">
                  {t(solution.descriptionKey)}
                </p>

                <div className="space-y-4">
                  {solution.features.map((feat, fidx) => (
                    <div key={fidx} className="flex items-center gap-4 group">
                      <div className={`p-1 rounded-full bg-slate-100 ${solution.accent} group-hover:scale-110 transition-transform`}>
                        <CheckCircle className="h-5 w-5" />
                      </div>
                      <span className="text-base font-medium text-slate-700">{t(feat)}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <a href={solution.link || "https://app.fukkoni.com/register"} className={`inline-flex items-center font-bold text-lg ${solution.accent} hover:underline decoration-2 underline-offset-4 transition-all group`}>
                    {t("hero_button_getStarted")}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>

              {/* Image Showcase */}
              <div className="lg:w-1/2 w-full">
                <div className="relative group perspective-1000">
                  <div className={`absolute inset-0 bg-gradient-to-r ${solution.color.replace('bg-', 'from-')}/20 to-transparent blur-3xl transform -rotate-3 scale-95 opacity-0 group-hover:opacity-100 transition-opacity duration-700`}></div>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200/50 bg-white transform transition-transform duration-700 hover:rotate-1 hover:scale-[1.02]">
                     <img 
                       src={solution.image} 
                       alt={t(solution.titleKey)} 
                       className="w-full h-auto object-cover"
                     />
                     {/* Overlay sheen effect */}
                     <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
